import { createSlice } from "@reduxjs/toolkit";
import {
  createUserThunk,
  getEscuelasThunk,
  getPlantelesThunk,
  getUserByIdThunk,
  getUsersThunk,
  toggleUserActiveThunk,
  updateUserThunk,
} from "./users.thunks";

const initialState = {
  list: [],
  loading: false,
  error: null,
  page: 1,
  pageSize: 10,
  totalPages: 1,
  count: 0,
  next: null,
  previous: null,
  maxPageReached: 1,
  search: "",

  selectedUser: null,
  selectedUserLoading: false,
  selectedUserError: null,
  viewModalOpen: false,

  // Edit form modal
  formModalOpen: false,
  formLoading: false,
  formError: null,

  confirmModalOpen: false,
  toggleUserId: null,
  toggleNextState: null,
  toggling: false,

  escuelas: [],
  planteles: [],
  institucionesLoading: false,
  institucionesError: null,

  escuelaFilter: null,
  plantelFilter: null,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUsersError: (state) => {
      state.error = null;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 1;
    },
    openViewModal: (state) => {
      state.viewModalOpen = true;
    },
    closeViewModal: (state) => {
      state.viewModalOpen = false;
      state.selectedUser = null;
    },
    openFormModal: (state) => {
      state.formModalOpen = true;
      state.formError = null;
    },
    closeFormModal: (state) => {
      state.formModalOpen = false;
      state.formError = null;
    },
    openConfirmToggle: (state, action) => {
      state.confirmModalOpen = true;
      state.toggleUserId = action.payload.id;
      state.toggleNextState = action.payload.isActive;
    },
    closeConfirmToggle: (state) => {
      state.confirmModalOpen = false;
      state.toggleUserId = null;
      state.toggleNextState = null;
    },
    setEscuelaFilter: (state, action) => {
      state.escuelaFilter = action.payload;
      state.plantelFilter = null; // resetea plantel al cambiar escuela
      state.planteles = [];
      state.page = 1;
    },
    setPlantelFilter: (state, action) => {
      state.plantelFilter = action.payload;
      state.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersThunk.fulfilled, (state, action) => {
        const { results, count, next, previous } = action.payload;

        state.loading = false;
        state.list = results;
        state.count = count;
        state.next = next;
        state.previous = previous;

        state.maxPageReached = Math.max(state.maxPageReached, state.page);
      })
      .addCase(getUsersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      /* ===== CREATE USER ===== */
      .addCase(createUserThunk.pending, (state) => {
        state.creating = true;
      })
      .addCase(createUserThunk.fulfilled, (state, action) => {
        state.creating = false;
        state.list.unshift(action.payload); // opcional
      })
      .addCase(createUserThunk.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload;
      })

      /**==== View user ===== */
      .addCase(getUserByIdThunk.pending, (state) => {
        state.selectedUserLoading = true;
        state.selectedUserError = null;
      })
      .addCase(getUserByIdThunk.fulfilled, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUser = action.payload;
      })
      .addCase(getUserByIdThunk.rejected, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUserError = action.payload;
      })
      // UPDATE
      .addCase(updateUserThunk.pending, (state) => {
        state.formLoading = true;
        state.formError = null;
      })
      .addCase(updateUserThunk.fulfilled, (state, action) => {
        state.formLoading = false;
        state.formModalOpen = false;
        const index = state.list.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.formLoading = false;
        state.formError = action.payload;
      })
      /**==== Inactive user */
      .addCase(toggleUserActiveThunk.pending, (state) => {
        state.toggling = true;
      })
      .addCase(toggleUserActiveThunk.fulfilled, (state, action) => {
        state.toggling = false;

        const id = state.toggleUserId;
        const nextState = state.toggleNextState;

        const user = state.list.find((u) => u.id === id);
        if (user) user.is_active = nextState;

        if (state.selectedUser?.id === id) {
          state.selectedUser.is_active = nextState;
        }

        state.confirmModalOpen = false;
      })
      .addCase(toggleUserActiveThunk.rejected, (state) => {
        state.toggling = false;
      })
      // ===== ESCUELAS =====
      .addCase(getEscuelasThunk.pending, (state) => {
        state.institucionesLoading = true;
        state.institucionesError = null;
      })
      .addCase(getEscuelasThunk.fulfilled, (state, action) => {
        state.institucionesLoading = false;
        state.escuelas = action.payload;
      })
      .addCase(getEscuelasThunk.rejected, (state, action) => {
        state.institucionesLoading = false;
        state.institucionesError = action.payload;
      })

      // ===== PLANTELES =====
      .addCase(getPlantelesThunk.pending, (state) => {
        state.institucionesLoading = true;
        state.institucionesError = null;
      })
      .addCase(getPlantelesThunk.fulfilled, (state, action) => {
        state.institucionesLoading = false;
        state.planteles = action.payload;
      })
      .addCase(getPlantelesThunk.rejected, (state, action) => {
        state.institucionesLoading = false;
        state.institucionesError = action.payload;
      });
  },
});

export const {
  clearUsersError,
  setPage,
  setSearch,
  openViewModal,
  closeViewModal,
  openFormModal,
  closeFormModal,
  openConfirmToggle,
  closeConfirmToggle,
  setEscuelaFilter,
  setPlantelFilter,
} = usersSlice.actions;
export default usersSlice.reducer;
